import React, { useEffect, useRef, useState } from "react";
import PageTitle from "../../shared/components/pagetitle";
import {
  CHEVRON_DOWN,
  CHEVRON_LEFT,
  CLOSE,
  DATE,
  EMPTY_SEARCH_2,
  FLAG,
  SEARCH_INPUT_ICON,
  TRASH_CAN,
} from "../../assets";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "react-query";
import {
  getAdminCaseState,
  getAdminUserById,
  getAllActivityLogs,
  getSinglectivityLogs,
} from "../../Services/admin.service";
import { Loader2 } from "lucide-react";
import ButtonV2 from "../../shared/components/buttonV2";
import { Case } from "../../types";
import moment from "moment";

const COLORS = ["#00A63E", "#C4A605"]; // Green and Gold

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ percent }: any) =>
  `${(percent * 100).toFixed(0)}%`;

export const CustomToolTip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white p-2 rounded-md shadow-md text-xs text-gray-800">
        <p className="font-semibold">{payload[0].name}</p>
        <p>Value: {payload[0].value}</p>
      </div>
    );
  }

  return null;
};

const UserItem = ({ userId }: { userId: number | string }) => {
  const {
    data: User,

    isLoading: userLoading,
  } = useQuery(["users"], () => getAdminUserById(userId));
  return (
    <span className="text-[#2A2E34] font-medium text-sm">
      {userLoading ? "..." : User?.name?.replace(/_/g, " ")}
    </span>
  );
};
function ActivityLogs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedActivityLogId, setSelectedActivityLogId] = useState<
    number | null
  >(null);
  const { data: activityLogs, isLoading } = useQuery(
    ["logs", currentPage],
    () => getAllActivityLogs(currentPage)
  );
  const { data: caseStats } = useQuery("stats", getAdminCaseState);
  const { data: activityLogsDetails, isLoading: loadingDetails } = useQuery(
    ["activity-details", selectedActivityLogId],
    () => getSinglectivityLogs(selectedActivityLogId),
    {
      enabled: selectedActivityLogId !== null,
    }
  );
  const pagesRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const data = [
    { name: "Total Upload", value: caseStats?.total_uploaded },
    { name: "Total Update", value: caseStats?.total_updated },
  ];
  useEffect(() => {
    const selectedButton = pagesRefs.current[currentPage];
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [currentPage]);

  return (
    <div className="p-3 sm:p-5">
      <PageTitle
        title="Activity Logs"
        subtitle="Track your searches and manage overall activities"
      />
      <div className="w-full flex flex-col lg:flex-row items-start gap-2 mt-5">
        <div className="w-full lg:w-[400px] border h-full border-borderColor rounded-xl">
          <div className="w-full flex items-center justify-between p-3 bg-lightGold rounded-t-xl">
            <div className="flex items-center gap-x-1">
              <img src={FLAG} className="w-[20px] h-[20px]" alt="" />
              <span className="text-sm sm:text-[16px] font-medium">
                Summary
              </span>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center py-5 px-3">
            <div className="w-full max-w-[300px] h-[200px] sm:h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomToolTip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-col gap-y-2 mt-3 w-full">
              {data.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    <span className="text-xs sm:text-sm font-medium text-[#2A2E34]">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-bold text-[#2A2E34]">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full border border-borderColor rounded-xl">
          <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-lightGold rounded-t-xl gap-2">
            <div className="flex items-center gap-x-1">
              {/* <img src={TRASH_CAN} className="w-[20px] h-[20px]" alt="" /> */}
              <span className="text-sm sm:text-[16px] font-medium">
                Activity Logs
              </span>
            </div>
            <div className="w-full sm:w-fit items-center gap-x-2 flex px-3 py-2 rounded-xl border border-borderColor">
              <img
                src={SEARCH_INPUT_ICON}
                className="w-[20px] h-[20px] flex-shrink-0"
                alt=""
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-transparent placeholder:text-sm outline-none"
                placeholder="Search activity logs"
              />
            </div>
          </div>

          <div className="mt-2">
            {activityLogs?.data?.length > 0 ? (
              <div>
                {activityLogs?.data
                  ?.filter((item: any) =>
                    item.description
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase())
                  )
                  ?.map((item: any) => (
                    <div
                      key={item.id}
                      className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-b border-borderColor gap-3"
                    >
                      <div className="flex flex-col gap-y-1 w-full min-w-0">
                        <span className="text-sm font-medium text-[#2A2E34] break-words">
                          {item.description}
                        </span>
                        <span className="text-xs font-normal text-primary-50">
                          {moment(item.created_at).format("MMM D, YYYY h:mm A")}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-2 flex-shrink-0">
                        <button
                          onClick={() => setSelectedActivityLogId(item.id)}
                          className="bg-globalGold hover:scale-95 transition-all duration-300 px-3 py-1 rounded-lg"
                        >
                          <span className="text-xs font-medium text-white">
                            Review
                          </span>
                        </button>
                      </div>
                    </div>
                  ))}

                <div className="flex flex-col sm:flex-row items-center justify-between pt-3 px-3 sm:px-5 py-4 gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage(activityLogs?.meta?.current_page - 1)
                    }
                    disabled={activityLogs?.meta?.current_page === 1}
                    className={`border border-[#EBEBEB] rounded-xl w-[40px] h-[40px] lg:flex hidden items-center gap-x-2 justify-center ${
                      activityLogs?.meta?.current_page === 1 && ""
                    }`}
                  >
                    <img
                      src={CHEVRON_LEFT}
                      className="w-[20px] h-[20px] "
                      alt=""
                    />
                  </button>
                  <div
                    className={`flex items-center gap-x-2 ${
                      activityLogs?.meta?.links?.slice(1, -1).length > 5 &&
                      "max-w-[200px] overflow-x-auto"
                    }`}
                  >
                    {activityLogs?.meta?.links
                      ?.slice(1, -1)
                      .map((page: any, index: number) => (
                        <button
                          ref={(el) => (pagesRefs.current[index] = el)}
                          onClick={() => {
                            page.url && setCurrentPage(Number(page.label));
                          }}
                          className={`flex items-center justify-center pt-[0.5px] hover:bg-globalGold hover:text-white transition-all duration-300 gap-x-3 min-w-[40px] h-[40px] rounded-xl ${
                            page.active
                              ? "bg-globalGold text-white"
                              : "border text-[#2A2E34]"
                          } `}
                        >
                          <span className={`text-[13px]`}>{page.label}</span>
                        </button>
                      ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage(activityLogs?.meta?.current_page + 1)
                    }
                    disabled={
                      activityLogs?.meta?.current_page ===
                      activityLogs?.meta?.last_page
                    }
                    className={`border border-[#EBEBEB] rounded-xl gap-x-2 w-[40px] h-[40px] lg:flex hidden items-center justify-center ${
                      activityLogs?.meta?.current_page ===
                        activityLogs?.meta?.last_page && ""
                    }`}
                  >
                    <img
                      src={CHEVRON_LEFT}
                      className="w-[20px] h-[20px] scale-x-[-1]"
                      alt=""
                    />
                  </button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 color="#9ca3af" size={30} className="animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-y-2 pb-5 justify-center items-center w-full px-4">
                <img
                  src={EMPTY_SEARCH_2}
                  className="w-24 h-20 sm:w-[147.69px] sm:h-[120px]"
                  alt=""
                />
                <span className="text-lg sm:text-xl font-bold text-customDark text-center">
                  No Activity Logs
                </span>
                <span className="text-sm font-medium text-primary-50 max-w-96 text-center">
                  No activity logs found
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      <>
        <div
          className={`w-full h-full fixed ${
            selectedActivityLogId ? "block" : "hidden"
          } top-0 left-0 bg-black flex items-center justify-center bg-opacity-50 z-[999]`}
        >
          {selectedActivityLogId && (
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="w-[600px] bg-white rounded-xl shadow-xl right-6 z-50"
            >
              <div className="w-full flex items-center justify-between bg-lightGold rounded-xl p-5">
                <h2 className="text-[16px] font-semibold text-[#2A2E34]">
                  Record Details
                </h2>
                <button
                  onClick={() => setSelectedActivityLogId(null)}
                  className="w-[30px] h-[30px] rounded-full -mt-2 flex items-center justify-center hover:bg-gray-200"
                >
                  <img src={CLOSE} className="w-[20px] h-[20px]" alt="" />
                </button>
              </div>
              {loadingDetails ? (
                <div className="flex items-center justify-center mt-10">
                  <Loader2 color="#9ca3af" className="animate-spin" size={30} />
                </div>
              ) : (
                <div className="p-5 max-h-[300px] overflow-y-scroll flex flex-col gap-y-5">
                  {activityLogsDetails?.user?.cases?.map((item: Case) => (
                    <div className="grid grid-cols-2 gap-6 border border-borderColor rounded-xl p-5">
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Property Title (certificate of occupancy)
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.title}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Registered Title number
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.title_number}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Location/Address of Property
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.address}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Survey plan number
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.survey_plan_number}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Name of Owner of property (optional)
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.owner_name}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Case Status
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.status}
                        </span>
                      </div>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium text-primary-50">
                          Last Updated
                        </span>
                        <span className="text-sm font-medium text-customDark">
                          {item.updated_at}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-end justify-end gap-x-3 p-8">
                <ButtonV2
                  title={"Close"}
                  btnStyle={"w-fit h-fit  px-7 py-2 rounded-xl bg-globalGold"}
                  textStyle={"text-sm font-medium text-white "}
                  handleClick={() => setSelectedActivityLogId(null)}
                />
              </div>
            </motion.div>
          )}
        </div>
      </>
    </div>
  );
}

export default ActivityLogs;
